import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { SectionEnum } from 'gql/graphql';
import type { BimChapterEnum, BimPriceLevelEnum } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    BimWorkOrderRowTypeEnum,
    getDetailsInPriceTableRequestId,
    totalsByConstructionTypeKey,
} from '../../WorkOrder/types';
import { bimWorkOrderChangeTotals } from '../bimWorkOrderChangeQueries';

type TUseBimWorkOrderChangeTotals = {
    workOrderChangeId: number | undefined;
    constructionTypeId: number | undefined;
    chapter: BimChapterEnum | string | undefined;
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    detailsRowType?: BimWorkOrderRowTypeEnum;
    useInPrices?: boolean;
    withEffect?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
};

export const useBimWorkOrderChangeTotals = ({
    workOrderChangeId,
    constructionTypeId,
    chapter,
    workOrderObjectId,
    workOrderChangeObjectId,
    useInPrices = true,
    withEffect = true,
    detailsRowType,
    priceLevel,
}: TUseBimWorkOrderChangeTotals) => {
    const dispatch = useDispatch();
    const requestId = getDetailsInPriceTableRequestId(useInPrices);

    const isTotalRowDetails = useMemo(() => detailsRowType === BimWorkOrderRowTypeEnum.TotalsString, [detailsRowType]);
    const isTotalsByConstructionType = useMemo(() => chapter === totalsByConstructionTypeKey, [chapter]);
    const isWorkOrderTotals = useMemo(() => chapter === SectionEnum.WorkOrderTotals, [chapter]);

    const totalVariables = useMemo(() => {
        if (isTotalsByConstructionType) {
            return {
                workOrderChangeId,
                constructionTypeId,
                chapter: undefined,
                useInPrices: false,
                priceLevel,
            };
        }

        if (isWorkOrderTotals) {
            return {
                workOrderChangeId,
                chapter: undefined,
                useInPrices: false,
                priceLevel,
            };
        }

        if (useInPrices || isTotalRowDetails) {
            return {
                workOrderChangeId,
                constructionTypeId,
                chapter: chapter as BimChapterEnum,
                workOrderObjectId: workOrderObjectId ?? null,
                workOrderChangeObjectId: workOrderChangeObjectId ?? null,
                useInPrices,
                priceLevel,
            };
        }

        return undefined;
    }, [
        chapter,
        constructionTypeId,
        isTotalRowDetails,
        isTotalsByConstructionType,
        isWorkOrderTotals,
        priceLevel,
        useInPrices,
        workOrderChangeId,
        workOrderChangeObjectId,
        workOrderObjectId,
    ]);

    const isRequestLegit = useMemo(() => {
        const canSend =
            Boolean(workOrderChangeId && chapter && (workOrderObjectId || workOrderChangeObjectId)) ||
            isTotalsByConstructionType ||
            isWorkOrderTotals;

        if (isTotalRowDetails && !useInPrices) {
            return canSend;
        }

        if (useInPrices) {
            return canSend;
        }

        return false;
    }, [
        chapter,
        isTotalRowDetails,
        isTotalsByConstructionType,
        isWorkOrderTotals,
        useInPrices,
        workOrderChangeId,
        workOrderChangeObjectId,
        workOrderObjectId,
    ]);
    const [makeReset] = useResetStoreData({
        requestId,
    });

    useEffect(() => {
        makeReset(undefined);
        if (!isTotalRowDetails && !useInPrices) {
            makeReset(undefined);
        }
    }, [isTotalRowDetails, makeReset, useInPrices]);

    const [data, status, , request] = useRequest({
        query: bimWorkOrderChangeTotals,
        requestId,
        variables: totalVariables,
        withEffect: Boolean(totalVariables && isRequestLegit && withEffect),
    });

    const makeRefreshTotalsData = useCallback(() => {
        dispatch(request(totalVariables));
    }, [dispatch, request, totalVariables]);

    const isLoading = status === RequestStatus.FETCHING;

    const rowTotalsData = useMemo(
        () =>
            data?.bimWorkOrderChangeTotals?.items?.map((item) => ({
                ...item,
                isTotalRow: true,
            })) ?? [],
        [data],
    );

    return {
        rowTotalsData,
        isLoading,
        makeRefreshTotalsData,
    };
};
